// Presentation helpers for encoder job / output statuses.
//
// Status *text* is always rendered verbatim, exactly like the desktop table's
// status column. The map below only decides colour, and every key in it is a real
// value returned by:
//
//   GET /api/v1/projects/{projectId}/jobs/statuses  ->  jobStatusOptions[].value
//
// captured 2026-08-11 (ids 0-11). Nothing here is guessed. `Not Applicable` and
// `Packing` in that response are output-level states, so the same vocabulary
// covers job.status and encodingOutputJob.statusDescription.

const RUNNING = { variant: 'primary', state: 'running', ended: false }
const NEUTRAL = { variant: 'secondary', state: 'neutral', ended: false }

const STATUS_META = {
  // In flight.
  submitted: RUNNING, // id 1
  pending: RUNNING, // id 2
  processing: RUNNING, // id 3
  packing: RUNNING, // id 11

  // Terminal, with an unambiguous outcome.
  complete: { variant: 'success', state: 'succeeded', ended: true }, // id 4
  partiallycompleted: { variant: 'warning', state: 'partial', ended: true }, // id 10
  error: { variant: 'danger', state: 'failed', ended: true }, // id 5
  invalidrequest: { variant: 'danger', state: 'failed', ended: true }, // id 9
  canceled: { variant: 'secondary', state: 'canceled', ended: true }, // id 6

  // Filter aggregates and non-outcomes: shown verbatim, no colour claimed.
  anystatus: NEUTRAL, // id 0
  notcomplete: NEUTRAL, // id 7
  notapplicable: NEUTRAL, // id 8

  // Legacy alias. `partialycompleted` (single l) is what JobSummary hardcoded in
  // 2021; it matches no current API value, kept in case older deployments send it.
  partialycompleted: { variant: 'warning', state: 'partial', ended: true },
}

// Compared after normalizing, so `Invalid Request`, `invalid-request` and
// `INVALIDREQUEST` all resolve to the same entry.
const normalize = status => (status || '').toString().toLowerCase().replace(/[^a-z]/g, '')

export const ENDED_JOB_STATUSES = Object.keys(STATUS_META).filter(key => STATUS_META[key].ended)

/**
 * Whether a job has reached a state it will not leave on its own. Drives the
 * refresh affordances on both desktop and mobile.
 *
 * NOTE: this deliberately fixes a long-standing bug. JobSummary previously
 * hardcoded ['complete','partialycompleted','error','invalidrequest'] and compared
 * it against `job.status.toLowerCase()`, which strips neither the space nor the
 * `partialy` misspelling - so `Invalid Request` and `Partially Completed` never
 * matched, and `Canceled` was absent from the list entirely. Refresh therefore
 * stayed visible on finished jobs since 2021 (commit c6b200bdd). Normalizing here
 * means Refresh now correctly hides for Invalid Request, Partially Completed and
 * Canceled as well. Covered by tests/unit/views/encoder/jobs/job-status.spec.js.
 */
export const isEndedStatus = status => Boolean(STATUS_META[normalize(status)]?.ended)

/**
 * How a status should be presented.
 *
 * @param {string} status the status text as returned by the API.
 * @param {boolean|null} inFlight pass job.isCancelable when it is known. It is only
 *   consulted for a status missing from the map above - a job that can still be
 *   cancelled is still in flight - so a status added to the API later still reads
 *   correctly instead of falling back to grey.
 */
export const jobStatusMeta = (status, inFlight = null) => {
  const known = STATUS_META[normalize(status)]
  if (known) return known
  return inFlight === true ? RUNNING : NEUTRAL
}

export const isRunningStatus = (status, inFlight = null) => jobStatusMeta(status, inFlight).state === 'running'

// `2026-08-11T09:12:00` comes back without a zone marker but is UTC.
export const toUtcDate = value => {
  if (!value) return null
  const date = new Date(String(value).endsWith('Z') ? value : `${value}Z`)
  return Number.isNaN(date.getTime()) ? null : date
}

// Splits an input file path into the file name and the folders leading to it.
export const splitFilePath = filePath => {
  const raw = (filePath || '').toString().trim()
  if (!raw) return { fileName: '', folder: '' }

  const withoutQuery = raw.split(/[?#]/)[0].replace(/\/+$/, '')
  const segments = withoutQuery.split('/')
  const fileName = segments.pop() || withoutQuery

  return { fileName, folder: segments.join('/') }
}
